import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import cogoToast from '@successtar/cogo-toast'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import {
  isPlausibleNayaxTerminalId,
  normalizeNayaxTerminalId
} from '@renderer/utils/pointPayment'

/**
 * Payments settings, `connected` mode with a Nayax reader: which reader this
 * booth wakes up.
 *
 * Nayax has no reader list to pick from, so the operator types the serial
 * printed on the reader (or its Nayax machine id). Studio checks it before it
 * is saved (`POST /api/wallet/nayax/terminal`, which opens a Spark transaction
 * and stops there: nothing is charged), so a typo is caught here and not in
 * front of a guest. Stored on this machine only, like the camera and printer.
 */

const NAYAX_TERMINAL_URL = `${process.env.VITE_API_URL}/api/wallet/nayax/terminal`

export type NayaxAccountState =
  | { kind: 'loading' }
  | { kind: 'none' }
  | { kind: 'ready'; name: string; currency: string }
  | { kind: 'error' }

/**
 * Does the signed-in operator have a Nayax account in Studio? Asked when the
 * Payments settings open; it decides whether "Connected terminal" is offered
 * on a project that is not Mexican.
 */
export function useNayaxAccount(enabled: boolean): NayaxAccountState {
  const [state, setState] = useState<NayaxAccountState>({ kind: 'loading' })
  useEffect(() => {
    if (!enabled) return
    let cancelled = false
    void (async (): Promise<void> => {
      try {
        const response = await fetch(NAYAX_TERMINAL_URL, {
          credentials: 'include',
          cache: 'no-cache'
        })
        const body = (await response.json().catch(() => null)) as {
          status?: string
          account?: { name?: string; currency?: string }
        } | null
        if (cancelled) return
        if (!response.ok) setState({ kind: 'error' })
        else if (body?.status === 'ok' && body.account)
          setState({
            kind: 'ready',
            name: body.account.name || 'Nayax',
            currency: body.account.currency || ''
          })
        else setState({ kind: 'none' })
      } catch {
        if (!cancelled) setState({ kind: 'error' })
      }
    })()
    return (): void => {
      cancelled = true
    }
  }, [enabled])
  return state
}

interface NayaxTerminalFieldProps {
  account: NayaxAccountState
  primaryColor: string
  compact?: boolean
}

export const NayaxTerminalField: React.FC<NayaxTerminalFieldProps> = ({
  account,
  primaryColor,
  compact = false
}) => {
  const { t } = useTranslation()
  const terminalId = useSettingsStore((state) => state.cardTerminal.terminalId) || ''
  const setCardTerminal = useSettingsStore((state) => state.setCardTerminal)
  const [draft, setDraft] = useState(terminalId)
  const [checking, setChecking] = useState(false)
  const [problem, setProblem] = useState('')

  const textClass = compact ? 'text-xs' : 'text-sm'
  const hintClass = compact ? 'text-[11px]' : 'text-xs'
  const candidate = normalizeNayaxTerminalId(draft)
  const unchanged = candidate === terminalId && Boolean(terminalId)

  const pair = async (): Promise<void> => {
    if (!isPlausibleNayaxTerminalId(candidate)) {
      setProblem(
        t('settings.payments.nayaxSerialInvalid', 'Digits only: the serial on the reader, or its Nayax machine id.')
      )
      return
    }
    setChecking(true)
    setProblem('')
    try {
      const response = await fetch(NAYAX_TERMINAL_URL, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ terminalId: candidate })
      })
      const body = (await response.json().catch(() => null)) as {
        ok?: boolean
        reason?: string
        error?: string
      } | null
      if (!response.ok || !body?.ok) {
        setProblem(body?.reason || body?.error || `HTTP ${response.status}`)
        return
      }
      setCardTerminal({ provider: 'nayax', terminalId: candidate })
      cogoToast.success(t('settings.payments.terminalSaved', 'This booth now charges on that terminal'))
    } catch (error) {
      setProblem(error instanceof Error ? error.message : String(error))
    } finally {
      setChecking(false)
    }
  }

  const unpair = (): void => {
    setCardTerminal({ terminalId: '' })
    setDraft('')
    setProblem('')
    cogoToast.success(t('settings.payments.terminalCleared', 'No terminal paired with this booth'))
  }

  if (account.kind === 'loading') {
    return (
      <p role="status" className={`text-gray-500 ${textClass}`}>
        {t('settings.payments.nayaxLoading', 'Checking your Nayax account...')}
      </p>
    )
  }

  if (account.kind !== 'ready') {
    return (
      <p className={`rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-gray-700 ${textClass}`}>
        {account.kind === 'error'
          ? t('settings.payments.nayaxAccountError', 'Could not reach Studio to check your Nayax account.')
          : t('settings.payments.nayaxNoAccount', 'Connect your Nayax account in Studio first: Wallet → Add payment gateway → Nayax card reader.')}
      </p>
    )
  }

  return (
    <div className="space-y-2">
      <label className={`block font-medium text-gray-800 ${textClass}`} htmlFor="nayax-terminal-id">
        {t('settings.payments.nayaxSerial', 'Reader serial')}
      </label>
      <div className="flex flex-wrap items-stretch gap-2">
        <input
          id="nayax-terminal-id"
          type="text"
          inputMode="numeric"
          autoComplete="off"
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value)
            setProblem('')
          }}
          placeholder="0434334921100366"
          className="min-h-[44px] min-w-0 flex-1 rounded-md border border-gray-300 p-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
        />
        <button
          type="button"
          onClick={() => void pair()}
          disabled={checking || unchanged || !candidate}
          className={`min-h-[44px] rounded-md px-4 font-medium text-white disabled:opacity-50 ${textClass}`}
          style={{ backgroundColor: primaryColor }}
        >
          {checking
            ? t('settings.payments.nayaxChecking', 'Checking...')
            : t('settings.payments.nayaxPair', 'Check & pair')}
        </button>
      </div>
      {problem && (
        <p role="alert" className={`break-words text-red-700 ${hintClass}`}>
          {problem}
        </p>
      )}
      {terminalId && !problem && (
        <p className={`text-green-700 ${hintClass}`}>
          {t('settings.payments.nayaxPaired', 'Paired: {{serial}} on {{account}}', {
            serial: terminalId,
            account: account.name
          })}{' '}
          <button type="button" onClick={unpair} className="font-medium text-gray-600 underline underline-offset-2">
            {t('settings.payments.nayaxUnpair', 'Unpair')}
          </button>
        </p>
      )}
    </div>
  )
}
